import { useState, useCallback } from 'react';
import styled from '@emotion/styled';

export type DashboardVisibility = {
  dashboard: boolean;
  page: boolean;
};

export type TagDetailsDashboardPublishingSettingsProps = {
  label?: string;
  values: DashboardVisibility;
  /** If provided, checkboxes are interactive. Otherwise they are read-only. */
  onChange?: (values: DashboardVisibility) => void;
  disableDashboard?: boolean;
  disablePage?: boolean;
};

export const TagDetailsDashboardPublishingSettings = ({
  label = 'Dashboard Display Settings',
  values,
  onChange,
  disableDashboard = false,
  disablePage = false,
}: TagDetailsDashboardPublishingSettingsProps) => {
  const [state, setState] = useState<DashboardVisibility>(values);
  const readOnly = !onChange;

  const handleChange = useCallback(
    (key: keyof DashboardVisibility, checked: boolean) => {
      const next = { ...state, [key]: checked };
      setState(next);
      onChange?.(next);
    },
    [state, onChange]
  );

  return (
    <Root>
      <Label>{label}</Label>
      <Value>
        <OptionList>
          <OptionRow>
            <NativeCheckbox
              type="checkbox"
              checked={state.dashboard}
              disabled={readOnly || disableDashboard}
              onChange={(e) => handleChange('dashboard', e.target.checked)}
            />
            <OptionLabel disabled={readOnly || disableDashboard}>Display in Journey Grid</OptionLabel>
          </OptionRow>
          <OptionRow>
            <NativeCheckbox
              type="checkbox"
              checked={state.page}
              disabled={readOnly || disablePage}
              onChange={(e) => handleChange('page', e.target.checked)}
            />
            <OptionLabel disabled={readOnly || disablePage}>Publish in Journey Stage Deep Dive</OptionLabel>
          </OptionRow>
        </OptionList>
      </Value>
    </Root>
  );
};

TagDetailsDashboardPublishingSettings.displayName = 'TagDetails.DashboardPublishingSettings';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.div`
  font-family: var(--font-bold);
  color: var(--gray-d);
  font-size: 14px;
`;

const Value = styled.div`
  min-height: 20px;
  display: flex;
  align-items: flex-start;
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

type NativeCheckboxProps = React.InputHTMLAttributes<HTMLInputElement>;

const NativeCheckbox = styled.input<NativeCheckboxProps>`
  margin: 0;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  accent-color: var(--pri-02);
`;

import type React from 'react';

const OptionLabel = styled.span<{ disabled?: boolean }>`
  font-size: 14px;
  color: ${(p) => (p.disabled ? 'var(--gray-s)' : 'var(--black)')};
`;
